import { serializeJsonLd } from "@/lib/safeJsonLd";
import { Link } from "react-router-dom";
import { Gift, ArrowLeft } from "lucide-react";
import { lazy, Suspense } from "react";
import EditableText from "@/components/EditableText";
import CrawlerContent from "@/components/CrawlerContent";
import { businessLastUpdatedHe } from "@/config/business";
import HeroBanner from "@/components/HeroBanner";
import FeatureStrip from "@/components/FeatureStrip";
const CategoryGrid = lazy(() => import("@/components/CategoryGrid"));
import SEOHead from "@/components/SEOHead";
import HomeDashboard from "@/components/HomeDashboard";

const ProductGrid = lazy(() => import("@/components/ProductGrid"));
import { useAuth } from "@/hooks/useAuth";
import { usePoints } from "@/hooks/usePoints";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { productUrl } from "@/lib/productUrl";

const indexJsonLd = {
  "@type": "Store",
  "name": "NiceGift",
 "description": "זירת מסחר ישראלית למתנות ומוצרים מותאמים אישית, משלוח מספקים מקומיים ותשלום מאובטח",
  "url": "https://www.nicegift.co.il",
  "logo": "https://www.nicegift.co.il/pwa-512x512.png",
  "currenciesAccepted": "ILS",
  "paymentAccepted": "Credit Card",
  "priceRange": "₪₪",
  "address": {
    "@type": "PostalAddress",
    "addressCountry": "IL",
  },
  "hasOfferCatalog": {
    "@type": "OfferCatalog",
 "name": "מתנות מותאמות אישית",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "אלקטרוניקה" },
      { "@type": "ListItem", "position": 2, "name": "אופנה" },
      { "@type": "ListItem", "position": 3, "name": "בית וגן" },
      { "@type": "ListItem", "position": 4, "name": "יופי וטיפוח" },
      { "@type": "ListItem", "position": 5, "name": "ספורט" },
      { "@type": "ListItem", "position": 6, "name": "מחשבים" },
      { "@type": "ListItem", "position": 7, "name": "שעונים" },
      { "@type": "ListItem", "position": 8, "name": "נעליים" },
      { "@type": "ListItem", "position": 9, "name": "תינוקות" },
    ],
  },
};

const PointsBanner = () => {
  const { user } = useAuth();
  const { balance, isLoading } = usePoints();

  if (!user || isLoading) return null;

  return (
    <Link
      to="/נקודות"
      className="block mx-auto max-w-5xl px-4 mt-4"
    >
      <div className="flex items-center justify-between gap-3 bg-gradient-to-l from-primary/15 via-primary/5 to-transparent border border-primary/20 rounded-2xl px-5 py-3 hover:border-primary/40 hover:shadow-[0_4px_24px_hsl(var(--primary)/0.15),0_0_0_1px_hsl(var(--primary)/0.1)] transition-all duration-300 group">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-primary/15 flex items-center justify-center">
            <Gift size={18} className="text-primary" />
          </div>
          <div>
            <p className="text-sm font-bold text-foreground">יש לך <span className="text-primary">{balance.toLocaleString()}</span> נקודות</p>
            <p className="text-[11px] text-muted-foreground">100 נקודות = ₪1 הנחה • לחץ לצפייה בהיסטוריה</p>
          </div>
        </div>
        <ArrowLeft size={16} className="text-primary group-hover:-translate-x-1 transition-transform" />
      </div>
    </Link>
  );
};

const Index = () => {
  const { data: featuredProducts } = useQuery({
    queryKey: ["homepage-featured-products"],
    queryFn: async () => {
      const { data } = await supabase
        .from("products")
        .select("id, name, price, original_price, image_url, short_description, stock, rating, reviews_count, slug")
        .eq("is_published", true).gt("stock", 0)
        .gt("price", 0)
        .order("sold_count", { ascending: false })
        .limit(50);
      return data ?? [];
    },
    staleTime: 5 * 60 * 1000,
  });

  return (
    <div className="min-h-screen bg-background font-heebo relative overflow-x-hidden">
      {/* ── Ambient Glow Orbs ── */}
      <div className="pointer-events-none fixed inset-0 z-0 overflow-hidden" aria-hidden="true">
        <div className="absolute top-[20vh] -right-32 w-[500px] h-[500px] rounded-full bg-primary/8 blur-[160px] animate-pulse" style={{ animationDuration: '6s' }} />
        <div className="absolute top-[55vh] -left-40 w-[400px] h-[400px] rounded-full bg-accent/10 blur-[140px] animate-pulse" style={{ animationDuration: '8s', animationDelay: '2s' }} />
        <div className="absolute top-[85vh] right-1/4 w-[350px] h-[350px] rounded-full bg-primary/6 blur-[120px] animate-pulse" style={{ animationDuration: '7s', animationDelay: '4s' }} />
        <div className="absolute top-[130vh] -left-20 w-[450px] h-[450px] rounded-full bg-[hsl(280,60%,50%)]/8 blur-[150px] animate-pulse" style={{ animationDuration: '9s', animationDelay: '1s' }} />
        <div className="absolute top-[170vh] right-10 w-[300px] h-[300px] rounded-full bg-accent/8 blur-[130px] animate-pulse" style={{ animationDuration: '5s', animationDelay: '3s' }} />
      </div>

      <SEOHead
        keywords="מתנות מותאמות אישית, מתנות, חנות מתנות, nicegift, מתנה מקורית, מתנות לעסקים, מתנות ליום הולדת, מתנות לחגים, משלוח מתנות, ישראל"
        jsonLd={indexJsonLd}
      />

      {/* JSON-LD ItemList for featured products */}
      {featuredProducts && featuredProducts.length > 0 && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: serializeJsonLd({
              "@context": "https://schema.org",
              "@type": "ItemList",
              name: "מוצרים פופולריים - NiceGift",
              description: "המוצרים הנמכרים ביותר באתר NiceGift",
              numberOfItems: featuredProducts.length,
              itemListElement: featuredProducts.map((p, i) => ({
                "@type": "ListItem",
                position: i + 1,
                item: {
                  "@type": "Product",
                  name: p.name,
                  url: `https://www.nicegift.co.il${productUrl(p.name)}`,
                  image: p.image_url || undefined,
                  description: p.short_description || undefined,
                  offers: {
                    "@type": "Offer",
                    price: p.price,
                    priceCurrency: "ILS",
                    availability: p.stock > 0 ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
                  },
                  ...(p.rating > 0 ? {
                    aggregateRating: { "@type": "AggregateRating", ratingValue: p.rating, reviewCount: p.reviews_count || 1 },
                  } : {}),
                },
              })),
            }),
          }}
        />
      )}

      <main>
          <HeroBanner />
        <PointsBanner />
        
        <FeatureStrip />

        <HomeDashboard />

        {/* Glow divider */}
        <div className="relative py-2" aria-hidden="true">
          <div className="mx-auto max-w-3xl h-px bg-gradient-to-r from-transparent via-primary/40 to-transparent" />
          <div className="mx-auto max-w-xl h-px mt-px bg-gradient-to-r from-transparent via-primary/20 to-transparent blur-sm" />
        </div>

        <Suspense fallback={<div className="min-h-[200px]" />}>
          <CategoryGrid />

          {/* Glow divider */}
          <div className="relative py-4" aria-hidden="true">
            <div className="mx-auto max-w-2xl h-px bg-gradient-to-r from-transparent via-accent/30 to-transparent" />
            <div className="mx-auto max-w-lg h-[2px] mt-px bg-gradient-to-r from-transparent via-accent/15 to-transparent blur-md" />
          </div>

          

          {/* Glow divider */}
          <div className="relative py-4" aria-hidden="true">
            <div className="mx-auto max-w-3xl h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent" />
            <div className="mx-auto max-w-xl h-[2px] mt-px bg-gradient-to-r from-transparent via-primary/15 to-transparent blur-md" />
          </div>

          <ProductGrid />
        </Suspense>

        {/* Vendor Recruitment Banner */}
        <section className="container max-w-5xl mx-auto px-4 py-6">
          <Link
            to="/הפוך-לספק"
            className="block relative overflow-hidden rounded-2xl border border-primary/25 group hover:border-primary/50 transition-all duration-500"
            style={{
              background: "linear-gradient(135deg, hsl(var(--primary) / 0.06), hsl(var(--card)), hsl(var(--accent) / 0.04))",
              boxShadow: "0 4px 30px hsl(var(--primary) / 0.08)",
            }}
          >
            {/* Decorative elements */}
            <div className="absolute top-0 inset-x-0 h-[2px] bg-gradient-to-r from-transparent via-primary/60 to-transparent" />
            <div className="absolute -top-20 -right-20 w-40 h-40 bg-primary/10 rounded-full blur-3xl pointer-events-none" />
            <div className="absolute -bottom-16 -left-16 w-36 h-36 bg-accent/10 rounded-full blur-3xl pointer-events-none" />

            <div className="relative flex flex-col md:flex-row items-center gap-5 p-6 md:p-8">
              {/* Icon */}
              <div
                className="w-16 h-16 md:w-20 md:h-20 rounded-2xl bg-primary/15 flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform duration-500"
                style={{ boxShadow: "0 0 25px hsl(var(--primary) / 0.2)" }}
              >
                <span className="text-3xl md:text-4xl">🏪</span>
              </div>

              {/* Text */}
              <div className="flex-1 text-center md:text-right space-y-2">
                <h2 className="text-lg md:text-xl font-black text-foreground">
                  <EditableText contentKey="vendor-banner-title" defaultText="בעל עסק? יש לך מלאי מוצרים?" />
                  <span className="text-primary"> <EditableText contentKey="vendor-banner-title2" defaultText="הגיע הזמן למכור אונליין!" /></span>
                </h2>
                <p className="text-sm text-muted-foreground leading-relaxed max-w-2xl">
                  <EditableText contentKey="vendor-banner-desc" defaultText="פתח חנות דיגיטלית ב-NiceGift והצג את המוצרים שלך ללקוחות ברחבי הארץ. אנחנו מטפלים בשיווק, בתשלומים ובחשיפה, ואתה מתמקד במוצרים ובמלאי עדכני." multiline />
                </p>
              </div>

              {/* CTA */}
              <div
                className="shrink-0 px-6 py-3 rounded-xl font-bold text-sm text-primary-foreground transition-all duration-300 group-hover:scale-105"
                style={{
                  background: "linear-gradient(135deg, hsl(var(--primary)), hsl(var(--accent-glow)))",
                  boxShadow: "0 0 20px hsl(var(--primary) / 0.3)",
                }}
              >
                פתח חנות עכשיו 
              </div>
            </div>
          </Link>
        </section>

        {/* Bottom SEO content */}
        <section className="container max-w-5xl mx-auto px-4 py-8">
          <div className="relative overflow-hidden rounded-2xl p-7 md:p-10 space-y-5 border border-blue-500/30 bg-gradient-to-br from-[#0a1628] via-[#0d1f3c] to-[#091428] shadow-xl shadow-blue-900/20">
            {/* Neon glow effects */}
            <div className="absolute top-0 left-1/4 w-72 h-72 bg-blue-500/20 rounded-full blur-[100px] pointer-events-none" />
            <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-cyan-400/15 rounded-full blur-[80px] pointer-events-none" />
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-32 bg-blue-400/10 rounded-full blur-[60px] pointer-events-none" />
            
            {/* Scan line animation */}
            <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{ backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(59,130,246,0.5) 2px, rgba(59,130,246,0.5) 3px)', backgroundSize: '100% 4px' }} />
            
            {/* Top accent line */}
            <div className="absolute top-0 left-[10%] right-[10%] h-[2px] bg-gradient-to-r from-transparent via-blue-400/70 to-transparent" />

            <h2 className="relative text-xl md:text-2xl font-black text-white tracking-wide drop-shadow-[0_0_10px_rgba(59,130,246,0.3)]">
              <EditableText contentKey="seo-title" defaultText="✨ למה לקנות ב-NiceGift?" />
            </h2>
            <p className="relative text-blue-50/90 leading-[1.9] text-sm font-medium">
              <EditableText contentKey="seo-paragraph1" defaultText="אנחנו מאמינים שכל מתנה צריכה להיות חוויה. לכן אצלנו תמצאו לא רק מוצרים, אלא גם אפשרות להתאמה אישית. הוסיפו טקסט, תמונה, או עיצוב ייחודי לכל מוצר מקטגוריית ההתאמה האישית. המוצרים נמכרים ונשלחים על ידי ספקים ישראליים, ועלות המשלוח מוצגת לפני התשלום." multiline />
            </p>
            <p className="relative text-blue-50/90 leading-[1.9] text-sm font-medium">
              <EditableText contentKey="seo-paragraph2" defaultText="התשלום מתבצע בצורה מאובטחת בכרטיס אשראי או ב-PayPal, ניתן לבקש החזרה תוך 30 יום מקבלת המוצר בכפוף למדיניות (משלוח ההחזרה על הלקוח, למעט מוצר פגום). צוות שירות הלקוחות זמין בטלפון, באימייל ובוואטסאפ בשעות הפעילות." multiline />
            </p>

            {/* Bottom accent line */}
            <div className="absolute bottom-0 left-[15%] right-[15%] h-[1px] bg-gradient-to-r from-transparent via-cyan-400/50 to-transparent" />
          </div>
        </section>
      </main>

      <CrawlerContent
        title="NiceGift | מתנות מותאמות אישית"
        description="NiceGift היא זירת מסחר ישראלית למתנות ומוצרים מותאמים אישית, עם מוצרים מספקים מקומיים, תשלום מאובטח ומדיניות שקופה."
        schemaType="https://schema.org/Store"
        sections={[
          { heading: "אתר פעיל ומעודכן", body: `אתר NiceGift עודכן לאחרונה ב-${businessLastUpdatedHe}, עם מוצרים, מבצעים ומלאי המתעדכנים באופן שוטף.` },
          { heading: "משלוח מהיר", body: "משלוח רגיל 1-5 ימי עסקים בעלות ₪25 לכל ספק, משלוח מהיר 1-2 ימי עסקים בעלות ₪39. משלוח חינם מעל ₪500 לכל ספק." },
          { heading: "תשלום מאובטח", body: "תשלום בכרטיס אשראי או ב-PayPal באמצעות ספקי תשלום מאובטחים." },
          { heading: "מדיניות החזרות", body: "ניתן לבקש החזרה בתוך 30 יום בכפוף למדיניות. משלוח ההחזרה על הלקוח, למעט מוצר פגום או שגוי, וההחזר מבוצע בתוך 14 יום מקבלת המוצר חזרה." },
          { heading: "תמיכת לקוחות", body: "שירות לקוחות בטלפון, בוואטסאפ ובאימייל בשעות הפעילות." },
          { heading: "קטגוריות", body: "אלקטרוניקה, אופנה, בית וגן, יופי וטיפוח, תינוקות, ספורט, מחשבים, שעונים, נעליים." },
        ]}
        links={[
          { label: "קטגוריות", href: "/קטגוריות" },
          { label: "מבצעים", href: "/מבצעים" },
          { label: "מוצרים חדשים", href: "/מוצרים-חדשים" },
          { label: "בלוג", href: "/בלוג" },
          { label: "צור קשר", href: "/צור-קשר" },
        ]}
      />
    </div>
  );
};

export default Index;